import React from 'react';

/*
 * The equipment catalog the add-equipment steps pick from (GET
 * equipment/catalog, the file backend/Accusite/Api/Catalog/equipment-catalog.json),
 * and the helpers Android's equipment page uses on it
 * (EquipmentDetailViewModel.kt, EquipmentKit.kt, PhotoCapture.kt).
 */

export interface CatalogModel { name: string; id: string; aliases?: string[]; type?: string | null }
export interface CatalogBrand { name: string; label?: string | null; models: CatalogModel[] }
export interface CatalogType { name: string; label?: string | null; picker?: string; brands?: CatalogBrand[] }
export interface CatalogCategory { name: string; chooseType?: boolean; types: CatalogType[]; brands?: CatalogBrand[] }
export interface EquipmentCatalog { version: number; brandColors: Record<string, string>; categories: CatalogCategory[] }

/**
 * The category a machine of a typed-in kind is saved under. It is never a
 * category of its own on the picker; the kind typed is its type.
 */
export const CUSTOM_CATEGORY = 'Custom';

/** Android's typeLabel: the catalog's display names for three kinds. */
export const typeLabel = (type?: string | null) => {
  const t = type?.trim();
  if (!t) return undefined;
  return ({
    'Standard crane': 'Telescopic crane',
    'Knuckleboom crane': 'Knuckle-boom crane',
    'Skid steer / compact track loader': 'Skid steer',
  } as Record<string, string>)[t] ?? t;
};

/**
 * The brands the card offers for the picked kind: the type's, or (Loaders)
 * the category's, cut down to the models of that kind and to the brands that
 * make one.
 */
export function brandsFor(category: CatalogCategory, type: string): CatalogBrand[] {
  if (!category.brands) return category.types.find(t => t.name === type)?.brands ?? [];
  return category.brands
    .map(b => ({...b, models: b.models.filter(m => !m.type || m.type === type)}))
    .filter(b => b.models.length > 0);
}

/** Letters and digits only, so "TW 280" finds "TW280" and "sk-800" finds "SK800". */
export const searchKey = (text: string) => text.toLowerCase().replace(/[^a-z0-9+]/g, '');

/** The brand's model matches the search, by name or another name on the plate. */
export const matchesModel = (model: CatalogModel, query: string) => {
  const q = searchKey(query);
  return !q || searchKey([model.name, ...(model.aliases ?? [])].join(' ')).includes(q);
};

/** A brand button's livery: its colour, with dark or white text and a grey edge on very light colours. */
export function brandStyle(hex?: string): React.CSSProperties | undefined {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return undefined;
  const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = [1, 3, 5].map(i => lin(parseInt(hex.slice(i, i + 2), 16)));
  const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return {background: hex, color: l > 0.179 ? '#111111' : '#fff', borderColor: l > 0.7 ? '#a5aba7' : hex, fontWeight: 700};
}

/**
 * The nickname's example for what was picked. One per category; Other and
 * anything without a category take the machine's own kind.
 */
export const nicknamePlaceholder = (category?: string | null, type?: string | null) => ({
  Trucks: 'e.g. Big chip truck', Cranes: 'e.g. Big crane', Trailers: 'e.g. Big trailer', Chippers: 'e.g. Main chipper',
  'Stump Grinders': 'e.g. Big grinder', Loaders: 'e.g. Yard skid steer', Excavators: 'e.g. Mini ex', Lifts: 'e.g. Spider lift',
  Attachments: 'e.g. Skid steer grapple',
} as Record<string, string>)[category ?? ''] ?? (type?.trim() ? `e.g. Main ${typeLabel(type)!.toLowerCase()}` : 'e.g. Main machine');

/** Digits and one decimal point, two places at most: what a money field accepts. */
export function moneyInput(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot < 0) return cleaned.slice(0, 7);
  return `${cleaned.slice(0, dot).slice(0, 7)}.${cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2)}`;
}

/** 85 → "85", 82.5 → "82.50": a stored amount back in its field. */
export const plainNumber = (value: number) => Number.isInteger(value) ? String(value) : value.toFixed(2);

/** The − / + under a price: a dollar at a time, never below $0; an off-step amount lands on the next step first. */
export function stepped(value: string, up: boolean): string {
  const current = Number(value) || 0;
  const next = up ? Math.floor(current) + 1 : Math.ceil(current) - 1;
  return plainNumber(Math.max(0, next));
}

const VIN = /[A-HJ-NPR-Z0-9]{17}/g;
const SERIAL_LABEL = /^(?:S\s*\/\s*N|SN|SER(?:IAL)?\.?\s*(?:NO\.?|NUMBER|#)?|VIN|PIN)\s*[:#.-]?\s*/i;
const TOKEN = /[A-Z0-9][A-Z0-9-]{3,}[A-Z0-9]/g;
const digits = (s: string) => s.replace(/\D/g, '').length;

/**
 * Up to three things in a plate's text that look like a VIN or serial number
 * (Android's serialCandidates): a 17-character VIN first, then text next to a
 * "S/N" or "Serial" label, then the longest runs of letters and digits that
 * contain at least three digits.
 */
export function serialCandidates(lines: string[]): string[] {
  const upper = lines.map(l => l.trim().toUpperCase()).filter(Boolean);
  const found = new Set<string>();
  // A VIN may be printed with spaces in it.
  upper.forEach(line => (line.replace(/ /g, '').match(VIN) ?? []).forEach(v => found.add(v)));
  upper.forEach((line, index) => {
    const match = SERIAL_LABEL.exec(line);
    if (!match) return;
    const rest = line.slice(match[0].length).trim();
    // The number is after the label, or on the line under it.
    const number = rest || upper[index + 1] || '';
    const token = number.replace(/ /g, '').match(TOKEN)?.[0];
    if (token && digits(token) > 0) found.add(token);
  });
  upper.flatMap(line => line.match(TOKEN) ?? [])
    .filter(t => digits(t) >= 3)
    .sort((a, b) => b.length - a.length)
    .forEach(t => found.add(t));
  return Array.from(found).slice(0, 3);
}

/**
 * Reads a picture of the plate in the browser (Tesseract, loaded on first use)
 * and returns what looks like the number; nothing when it cannot read one.
 */
export async function readSerialCandidates(file: File): Promise<string[]> {
  try {
    const {createWorker} = await import('tesseract.js');
    const worker = await createWorker('eng');
    try {
      const {data} = await worker.recognize(file);
      return serialCandidates(data.text.split('\n'));
    } finally {
      await worker.terminate();
    }
  } catch {
    return [];
  }
}
